<script setup lang="ts">
import ClientList from "@/components/main/search-panel/ClientList.vue";
import ProjectSearchPanel from "@/components/main/search-panel/ProjectSearchPanel.vue";
import {computed, ref, watch, onMounted, onUnmounted} from "vue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import { hasEscapeClosableOverlay } from "@/composables/overlayStack";

const storageKey = 'search-block-positions_v1.2'

const emit = defineEmits(['collapse-change'])

const containerRef = ref<HTMLElement | null>(null)
const clientListRef = ref<InstanceType<typeof ClientList> | null>(null)
const projectSearchRef = ref<InstanceType<typeof ProjectSearchPanel> | null>(null)
const containerHeight = ref(0)

// Минимальная высота блока = высота его фильтров
const minTopHeight = ref(0)
const minBottomHeight = ref(0)

// Храним высоты в процентах
const topBlockHeightPercent = ref(0.5)
const bottomBlockHeightPercent = ref(0.5)

// Сохраняем размеры для возврата при разворачивании
const savedTopPercent = ref(0.5)
const savedBottomPercent = ref(0.5)

// Флаги свернутости
const collapsedTop = ref(false)
const collapsedBottom = ref(false)

// Вычисляемые высоты в пикселях
const topBlockHeightPx = computed(() => topBlockHeightPercent.value * containerHeight.value)
const bottomBlockHeightPx = computed(() => bottomBlockHeightPercent.value * containerHeight.value)

// Минимальные высоты в процентах (пока контейнер не измерен — 0, а не NaN)
const minTopHeightPercent = computed(() =>
	containerHeight.value ? minTopHeight.value / containerHeight.value : 0
)
const minBottomHeightPercent = computed(() =>
	containerHeight.value ? minBottomHeight.value / containerHeight.value : 0
)

const isVerticalResizing = ref(false)
const startY = ref(0)
const startTopHeightPercent = ref(0)
const startBottomHeightPercent = ref(0)
const initialCollapsedTop = ref(false)
const initialCollapsedBottom = ref(false)

const visualVerticalResizerTop = ref(0)
const visualVerticalResizerLeft = ref(0)
const visualVerticalResizerWidth = ref(0)

const bothExpanded = computed(() => !collapsedTop.value && !collapsedBottom.value)

const { isMobile, isTablet } = useBreakpoints()
const isCompactMode = computed(() => isMobile.value || isTablet.value)

const useResizeObserver = (element: HTMLElement, callback: () => void) => {
	let frame = 0
	const observer = new ResizeObserver((entries) => {
		cancelAnimationFrame(frame)
		frame = requestAnimationFrame(() => {
			if (Array.isArray(entries) && entries.length) {
				callback()
			}
		})
	})
	observer.observe(element)
	return observer
}

// Высота фильтров + вертикальные padding/border списка: на этой высоте область списка
// схлопывается в ноль. ceil по дробной высоте, иначе блок перерастёт себя на долю пикселя
// и у .vertical-block появится скролл. 0 — блок скрыт, замер недоступен.
const measureBlockMinHeight = (filters?: HTMLElement | null, body?: HTMLElement | null) => {
	if (!filters || !body) {
		return 0
	}
	const filtersHeight = filters.getBoundingClientRect().height
	if (!filtersHeight) {
		return 0
	}
	const bodyStyle = getComputedStyle(body)
	const bodyChrome = parseFloat(bodyStyle.paddingTop) + parseFloat(bodyStyle.paddingBottom)
		+ parseFloat(bodyStyle.borderTopWidth) + parseFloat(bodyStyle.borderBottomWidth)
	return Math.ceil(filtersHeight + bodyChrome)
}

const updateMinTopHeight = () => {
	const height = measureBlockMinHeight(clientListRef.value?.filtersEl, clientListRef.value?.bodyEl)
	if (height) {
		minTopHeight.value = height
	}
}

const updateMinBottomHeight = () => {
	const height = measureBlockMinHeight(projectSearchRef.value?.filtersEl, projectSearchRef.value?.bodyEl)
	if (height) {
		minBottomHeight.value = height
	}
}

// Фильтры выросли (теги, клиенты) или окно стало ниже — подтягиваем блок до минимума,
// иначе CSS min-height раздует его и второй блок обрежется.
const clampToMinHeights = () => {
	if (!bothExpanded.value || isVerticalResizing.value) {
		return
	}
	if (topBlockHeightPercent.value < minTopHeightPercent.value) {
		topBlockHeightPercent.value = minTopHeightPercent.value
		bottomBlockHeightPercent.value = 1 - minTopHeightPercent.value
	} else if (bottomBlockHeightPercent.value < minBottomHeightPercent.value) {
		bottomBlockHeightPercent.value = minBottomHeightPercent.value
		topBlockHeightPercent.value = 1 - minBottomHeightPercent.value
	}
}

const loadStateFromStorage = () => {
	try {
		const saved = localStorage.getItem(storageKey)
		if (saved) {
			return JSON.parse(saved)
		}
	} catch (error) {
		console.warn('Не удалось загрузить состояние из localStorage:', error)
	}
	return null
}

const saveStateToStorage = () => {
	try {
		const stateToSave = {
			topHeightPercent: topBlockHeightPercent.value,
			bottomHeightPercent: bottomBlockHeightPercent.value,
			savedTopPercent: savedTopPercent.value,
			savedBottomPercent: savedBottomPercent.value,
			collapsedTop: collapsedTop.value,
			collapsedBottom: collapsedBottom.value
		}
		localStorage.setItem(storageKey, JSON.stringify(stateToSave))
	} catch (error) {
		console.warn('Не удалось сохранить состояние в localStorage:', error)
	}
}

const initializeHeights = () => {
	if (!containerRef.value) return
	updateContainerHeight()

	const savedState = loadStateFromStorage()
	if (isMobile.value) {
		resetToNormal()
	} else if (savedState) {
		topBlockHeightPercent.value = savedState.topHeightPercent
		bottomBlockHeightPercent.value = savedState.bottomHeightPercent
		savedTopPercent.value = savedState.savedTopPercent || 0.5
		savedBottomPercent.value = savedState.savedBottomPercent || 0.5
		collapsedTop.value = savedState.collapsedTop || false
		collapsedBottom.value = savedState.collapsedBottom || false

		// При инициализации применяем collapsed состояния
		if (collapsedTop.value) {
			topBlockHeightPercent.value = 0
			bottomBlockHeightPercent.value = 1
		} else if (collapsedBottom.value) {
			bottomBlockHeightPercent.value = 0
			topBlockHeightPercent.value = 1
		}
	} else {
		resetToNormal()
	}

	// В compact mode всегда один свёрнут, другой развёрнут
	if (isTablet.value && !collapsedTop.value && !collapsedBottom.value) {
		savedTopPercent.value = topBlockHeightPercent.value
		savedBottomPercent.value = bottomBlockHeightPercent.value
		collapsedBottom.value = true
		collapsedTop.value = false
		topBlockHeightPercent.value = 1
		bottomBlockHeightPercent.value = 0
	}

	clampToMinHeights()
}

const updateContainerHeight = () => {
	if (containerRef.value) {
		containerHeight.value = containerRef.value.clientHeight
	}
}

const resetToNormal = () => {
	topBlockHeightPercent.value = 0.5
	bottomBlockHeightPercent.value = 0.5
	savedTopPercent.value = 0.5
	savedBottomPercent.value = 0.5
	collapsedTop.value = false
	collapsedBottom.value = false
}

const toggleMobileBlock = (block: 'top' | 'bottom') => {
	const clickedBlockIsFullscreen =
		(block === 'top' && !collapsedTop.value && collapsedBottom.value) ||
		(block === 'bottom' && collapsedTop.value && !collapsedBottom.value)

	if (clickedBlockIsFullscreen) {
		resetToNormal()
		return
	}

	const showTop = block === 'top'
	collapsedTop.value = !showTop
	collapsedBottom.value = showTop
	topBlockHeightPercent.value = showTop ? 1 : 0
	bottomBlockHeightPercent.value = 1 - topBlockHeightPercent.value
}

const toggleBlock = (block: 'top' | 'bottom') => {
	if (isMobile.value) {
		toggleMobileBlock(block)
		emitCollapseChange()
		return
	}

	if (block === 'top') {
		if (collapsedTop.value) {
			// Верхний был свернут - разворачиваем верхний и сворачиваем нижний
			// Запоминаем текущие размеры перед изменением
			savedTopPercent.value = topBlockHeightPercent.value
			savedBottomPercent.value = bottomBlockHeightPercent.value

			collapsedTop.value = false
			collapsedBottom.value = true

			// Применяем новые размеры
			topBlockHeightPercent.value = 1
			bottomBlockHeightPercent.value = 0
		} else {
			if (collapsedBottom.value) {
				if (isCompactMode.value) {
					// COMPACT: swap — свернуть верхний, развернуть нижний
					collapsedTop.value = true
					collapsedBottom.value = false
					topBlockHeightPercent.value = 0
					bottomBlockHeightPercent.value = 1
				} else {
					// Нижний свернут, верхний развернут - это состояние после первого клика
					// Возвращаемся к сохраненным размерам (до первого клика)
					collapsedTop.value = false
					collapsedBottom.value = false

					// Восстанавливаем размеры из savedTopPercent/savedBottomPercent
					topBlockHeightPercent.value = savedTopPercent.value
					bottomBlockHeightPercent.value = savedBottomPercent.value
					// Пока блок был развёрнут, его фильтры могли вырасти
					clampToMinHeights()
				}
			} else {
				// Оба развернуты - это исходное состояние
				// Запоминаем текущие размеры и сворачиваем нижний
				savedTopPercent.value = topBlockHeightPercent.value
				savedBottomPercent.value = bottomBlockHeightPercent.value

				collapsedTop.value = false
				collapsedBottom.value = true

				// Применяем новые размеры
				topBlockHeightPercent.value = 1
				bottomBlockHeightPercent.value = 0
			}
		}
	} else if (block === 'bottom') {
		if (collapsedBottom.value) {
			// Нижний был свернут - разворачиваем нижний и сворачиваем верхний
			// Запоминаем текущие размеры перед изменением
			savedTopPercent.value = topBlockHeightPercent.value
			savedBottomPercent.value = bottomBlockHeightPercent.value

			collapsedTop.value = true
			collapsedBottom.value = false

			// Применяем новые размеры
			bottomBlockHeightPercent.value = 1
			topBlockHeightPercent.value = 0
		} else {
			if (collapsedTop.value) {
				if (isCompactMode.value) {
					// COMPACT: swap — свернуть нижний, развернуть верхний
					collapsedBottom.value = true
					collapsedTop.value = false
					bottomBlockHeightPercent.value = 0
					topBlockHeightPercent.value = 1
				} else {
					// Верхний свернут, нижний развернут - это состояние после первого клика
					// Возвращаемся к сохраненным размерам (до первого клика)
					collapsedTop.value = false
					collapsedBottom.value = false

					// Восстанавливаем размеры из savedTopPercent/savedBottomPercent
					topBlockHeightPercent.value = savedTopPercent.value
					bottomBlockHeightPercent.value = savedBottomPercent.value
					// Пока блок был развёрнут, его фильтры могли вырасти
					clampToMinHeights()
				}
			} else {
				// Оба развернуты - это исходное состояние
				// Запоминаем текущие размеры и сворачиваем верхний
				savedTopPercent.value = topBlockHeightPercent.value
				savedBottomPercent.value = bottomBlockHeightPercent.value

				collapsedTop.value = true
				collapsedBottom.value = false

				// Применяем новые размеры
				bottomBlockHeightPercent.value = 1
				topBlockHeightPercent.value = 0
			}
		}
	}

	saveStateToStorage()
	emitCollapseChange()
}

const emitCollapseChange = () => {
	emit('collapse-change', {
		topCollapsed: collapsedTop.value,
		bottomCollapsed: collapsedBottom.value
	})
}

const startVerticalResize = (event: MouseEvent) => {
	if (isCompactMode.value) return
	event.preventDefault()

	if (!containerRef.value) return

	const containerRect = containerRef.value.getBoundingClientRect()
	visualVerticalResizerLeft.value = containerRect.left
	visualVerticalResizerWidth.value = containerRect.width

	isVerticalResizing.value = true
	startY.value = event.clientY
	startTopHeightPercent.value = topBlockHeightPercent.value
	startBottomHeightPercent.value = bottomBlockHeightPercent.value

	initialCollapsedTop.value = collapsedTop.value
	initialCollapsedBottom.value = collapsedBottom.value

	visualVerticalResizerTop.value = containerRect.top + topBlockHeightPx.value

	document.addEventListener('mousemove', handleVerticalResizeMove)
	document.addEventListener('mouseup', stopVerticalResize)
	document.body.style.userSelect = 'none'
	document.body.style.cursor = 'row-resize'
}

let resizeRafId: number | null = null
let lastMoveEvent: MouseEvent | null = null

const cancelResizeRaf = () => {
	if (resizeRafId !== null) {
		cancelAnimationFrame(resizeRafId)
		resizeRafId = null
	}
}

const applyVerticalResize = (event: MouseEvent) => {
	if (!isVerticalResizing.value || !containerRef.value) {
		return
	}

	const deltaY = event.clientY - startY.value
	const deltaPercent = deltaY / containerHeight.value

	let newTopHeightPercent = startTopHeightPercent.value + deltaPercent
	let newBottomHeightPercent = startBottomHeightPercent.value - deltaPercent

	// Определяем текущее состояние
	const currentTopCollapsed = collapsedTop.value
	const currentBottomCollapsed = collapsedBottom.value

	// Логика для ресайзера
	if (!currentTopCollapsed && !currentBottomCollapsed) {
		if (newTopHeightPercent < minTopHeightPercent.value) {
			collapsedTop.value = true
			newTopHeightPercent = 0
			newBottomHeightPercent = 1

			// При схватывании запоминаем размеры до схватывания
			if (!initialCollapsedTop.value) {
				savedTopPercent.value = startTopHeightPercent.value
				savedBottomPercent.value = startBottomHeightPercent.value
			}
		} else if (newBottomHeightPercent < minBottomHeightPercent.value) {
			collapsedBottom.value = true
			newBottomHeightPercent = 0
			newTopHeightPercent = 1

			// При схватывании запоминаем размеры до схватывания
			if (!initialCollapsedBottom.value) {
				savedTopPercent.value = startTopHeightPercent.value
				savedBottomPercent.value = startBottomHeightPercent.value
			}
		}
	} else if (currentTopCollapsed && !currentBottomCollapsed) {
		if (newTopHeightPercent >= minTopHeightPercent.value) {
			collapsedTop.value = false
			collapsedBottom.value = false

			if (newBottomHeightPercent < minBottomHeightPercent.value) {
				collapsedBottom.value = true
				newBottomHeightPercent = 0
				newTopHeightPercent = 1
			}
		} else if (newBottomHeightPercent < minBottomHeightPercent.value) {
			collapsedTop.value = false
			collapsedBottom.value = true
			newBottomHeightPercent = 0
			newTopHeightPercent = 1
		}
	} else if (!currentTopCollapsed && currentBottomCollapsed) {
		if (newBottomHeightPercent >= minBottomHeightPercent.value) {
			collapsedTop.value = false
			collapsedBottom.value = false

			if (newTopHeightPercent < minTopHeightPercent.value) {
				collapsedTop.value = true
				newTopHeightPercent = 0
				newBottomHeightPercent = 1
			}
		} else if (newTopHeightPercent < minTopHeightPercent.value) {
			collapsedTop.value = true
			collapsedBottom.value = false
			newTopHeightPercent = 0
			newBottomHeightPercent = 1
		}
	}

	// Проверяем общие ограничения для свернутых блоков
	if (collapsedTop.value && newTopHeightPercent > 0) {
		newTopHeightPercent = 0
		newBottomHeightPercent = 1
	}

	if (collapsedBottom.value && newBottomHeightPercent > 0) {
		newBottomHeightPercent = 0
		newTopHeightPercent = 1
	}

	// Гарантируем минимальные высоты для свернутых блоков
	if (newTopHeightPercent < 0) {
		newTopHeightPercent = 0
		newBottomHeightPercent = 1
	}

	if (newBottomHeightPercent < 0) {
		newBottomHeightPercent = 0
		newTopHeightPercent = 1
	}

	// Корректируем сумму процентов
	const totalPercent = newTopHeightPercent + newBottomHeightPercent
	if (Math.abs(totalPercent - 1) > 0.001) {
		newBottomHeightPercent = 1 - newTopHeightPercent
	}

	// Обновляем позицию визуального ресайзера
	const containerRect = containerRef.value.getBoundingClientRect()
	visualVerticalResizerTop.value = containerRect.top + (newTopHeightPercent * containerHeight.value)

	// Обновляем проценты
	topBlockHeightPercent.value = newTopHeightPercent
	bottomBlockHeightPercent.value = newBottomHeightPercent
}

const handleVerticalResizeMove = (event: MouseEvent) => {
	lastMoveEvent = event
	if (resizeRafId !== null) {
		return
	}
	resizeRafId = requestAnimationFrame(() => {
		resizeRafId = null
		if (lastMoveEvent) {
			applyVerticalResize(lastMoveEvent)
		}
	})
}

const stopVerticalResize = () => {
	if (!isVerticalResizing.value) {
		return
	}

	// Флашим последний отложенный кадр, чтобы итоговая позиция была точной
	if (resizeRafId !== null) {
		cancelResizeRaf()
		if (lastMoveEvent) {
			applyVerticalResize(lastMoveEvent)
		}
	}

	// Финальная корректировка после ресайза
	if (collapsedTop.value && topBlockHeightPercent.value > 0) {
		topBlockHeightPercent.value = 0
		bottomBlockHeightPercent.value = 1
	}

	if (collapsedBottom.value && bottomBlockHeightPercent.value > 0) {
		bottomBlockHeightPercent.value = 0
		topBlockHeightPercent.value = 1
	}

	if (!collapsedTop.value && topBlockHeightPercent.value < minTopHeightPercent.value) {
		topBlockHeightPercent.value = minTopHeightPercent.value
		bottomBlockHeightPercent.value = 1 - minTopHeightPercent.value
	}

	if (!collapsedBottom.value && bottomBlockHeightPercent.value < minBottomHeightPercent.value) {
		bottomBlockHeightPercent.value = minBottomHeightPercent.value
		topBlockHeightPercent.value = 1 - minBottomHeightPercent.value
	}

	// Сохраняем размеры после ресайза как сохраненные, если оба развернуты
	if (bothExpanded.value) {
		savedTopPercent.value = topBlockHeightPercent.value
		savedBottomPercent.value = bottomBlockHeightPercent.value
	}

	saveStateToStorage()
	emitCollapseChange()

	isVerticalResizing.value = false
	document.removeEventListener('mousemove', handleVerticalResizeMove)
	document.removeEventListener('mouseup', stopVerticalResize)
	document.body.style.userSelect = ''
	document.body.style.cursor = ''
}

const setCollapsedState = (top: boolean, bottom: boolean) => {
	collapsedTop.value = top
	collapsedBottom.value = bottom

	if (top) {
		topBlockHeightPercent.value = 0
		bottomBlockHeightPercent.value = 1
	} else if (bottom) {
		bottomBlockHeightPercent.value = 0
		topBlockHeightPercent.value = 1
	}

	saveStateToStorage()
	emitCollapseChange()
}

// Типы input без ввода текста: фокус на них остаётся после клика, но Escape им не адресован
const NON_TEXT_INPUT_TYPES = ['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image']

// Пока фокус в поле, Escape остаётся за ним: поле очищает текст или отменяет правку.
// Секции параметров (InlineDescriptor) фокусируемы и без contenteditable, Escape их отпускает.
const isFieldFocused = (target: EventTarget | null) => {
	if (!(target instanceof HTMLElement)) {
		return false
	}
	if (target.isContentEditable || target instanceof HTMLTextAreaElement) {
		return true
	}
	if (target instanceof HTMLInputElement) {
		return !NON_TEXT_INPUT_TYPES.includes(target.type)
	}
	return !!target.closest('.inline-descriptor')
}

// Escape при развёрнутом блоке работает как его кнопка «Свернуть», если закрывать по
// Escape больше нечего. Слушатель стоит на window в capture-фазе и подписан при монтировании
// панели, раньше модалок и подтверждений (они подписываются при открытии), поэтому
// видит открытые элементы до того, как их обработчики их закроют.
const handleEscape = (event: KeyboardEvent) => {
	if (event.key !== 'Escape') {
		return
	}
	if (isCompactMode.value || isVerticalResizing.value) {
		return
	}
	if (hasEscapeClosableOverlay() || isFieldFocused(event.target)) {
		return
	}
	if (collapsedBottom.value && !collapsedTop.value) {
		toggleBlock('top')
	} else if (collapsedTop.value && !collapsedBottom.value) {
		toggleBlock('bottom')
	}
}

// Обновляем высоту контейнера при изменении масштаба
const handleResizeWindow = () => {
	updateContainerHeight()
}

// При переходе в compact mode — принудительно один свёрнут
watch(isTablet, (tablet) => {
	if (tablet && !collapsedTop.value && !collapsedBottom.value) {
		savedTopPercent.value = topBlockHeightPercent.value
		savedBottomPercent.value = bottomBlockHeightPercent.value
		collapsedBottom.value = true
		collapsedTop.value = false
		topBlockHeightPercent.value = 1
		bottomBlockHeightPercent.value = 0
		saveStateToStorage()
		emitCollapseChange()
	}
})

watch(isMobile, (mobile) => {
	if (!mobile) return
	resetToNormal()
	emitCollapseChange()
})

// Минимум в процентах меняется и от высоты фильтров, и от высоты контейнера
watch([minTopHeightPercent, minBottomHeightPercent], () => {
	clampToMinHeights()
})

let resizeObserver: ResizeObserver | null = null
const filtersObservers: ResizeObserver[] = []

onMounted(() => {
	initializeHeights()
	window.addEventListener('resize', handleResizeWindow)
	window.addEventListener('keydown', handleEscape, true)

	if (containerRef.value) {
		resizeObserver = useResizeObserver(containerRef.value, () => {
			updateContainerHeight()
		})
	}

	// Скрытый при загрузке блок получит замер, когда станет видимым:
	// ResizeObserver срабатывает при переходе размера из 0 в реальный.
	const clientFilters = clientListRef.value?.filtersEl
	if (clientFilters) {
		filtersObservers.push(useResizeObserver(clientFilters, updateMinTopHeight))
	}
	const projectFilters = projectSearchRef.value?.filtersEl
	if (projectFilters) {
		filtersObservers.push(useResizeObserver(projectFilters, updateMinBottomHeight))
	}
})

onUnmounted(() => {
	cancelResizeRaf()
	document.removeEventListener('mousemove', handleVerticalResizeMove)
	document.removeEventListener('mouseup', stopVerticalResize)
	window.removeEventListener('resize', handleResizeWindow)
	window.removeEventListener('keydown', handleEscape, true)

	if (resizeObserver) {
		resizeObserver.disconnect()
	}
	filtersObservers.forEach(observer => observer.disconnect())
})

defineExpose({
	resetToNormal,
	setCollapsedState,
	toggleBlock
})
</script>

<template>
	<div class="vertical-container" ref="containerRef">
		<div
			v-show="!collapsedTop"
			class="vertical-block top-block"
			:style="{
				height: topBlockHeightPx + 'px',
				minHeight: minTopHeight + 'px'
			}"
		>
			<div class="block-content">
				<client-list
					ref="clientListRef"
					:is-expanded="collapsedBottom"
					@on-toggle="toggleBlock('top')"
				/>
			</div>
		</div>

		<div
			v-show="bothExpanded"
			class="vertical-resizer"
			:class="{ 'vertical-resizer--disabled': isCompactMode }"
			@mousedown="startVerticalResize($event)"
		></div>

		<div
			v-show="!collapsedBottom"
			class="vertical-block bottom-block"
			:style="{
				height: bottomBlockHeightPx + 'px',
				minHeight: minBottomHeight + 'px'
			}"
		>
			<div class="block-content">
				<project-search-panel
					ref="projectSearchRef"
					:is-expanded="collapsedTop"
					@on-toggle="toggleBlock('bottom')"
				/>
			</div>
		</div>

		<div
			v-if="isVerticalResizing"
			class="visual-resizer vertical"
			:style="{
				top: visualVerticalResizerTop + 'px',
				left: visualVerticalResizerLeft + 'px',
				width: visualVerticalResizerWidth + 'px'
			}"
		></div>
	</div>
</template>

<style scoped lang="scss">
.vertical-container {
	position: relative;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.vertical-block {
	position: relative;
	width: 100%;
	overflow: auto;
}

.block-content {
	display: flex;
	flex-direction: column;
	flex: 1;
	height: 100%;
	padding: 0;
}

/* Вертикальный ресайзер */
.vertical-resizer {
	position: relative;
	height: 8px;
	background: transparent;
	cursor: row-resize;
	z-index: 10;
	opacity: 0;
}

.vertical-resizer:hover {
	opacity: 1;
	background: rgba(57, 103, 66, 0.2);
}

.vertical-resizer::after {
	content: '';
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	height: 4px;
	width: 40px;
	background: rgba(57, 103, 66, 0.4);
	border-radius: 1px;
}

.vertical-resizer:hover::after {
	background: rgba(57, 103, 66, 0.4);
	height: 4px;
	width: 60px;
	border-radius: 6px;
}

.vertical-resizer--disabled {
	cursor: default;
	pointer-events: none;
}

/* Визуальный вертикальный ресайзер */
.visual-resizer.vertical {
	position: fixed;
	height: 8px;
	width: 100%;
	background: transparent;
	cursor: row-resize;
	z-index: 1000;
	pointer-events: none;
}
</style>
